import React, { useReducer, useRef, useCallback, useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import useMouse from 'react-use/lib/useMouse';

import {
  ADD_APP,
  DEL_APP,
  FOCUS_APP,
  MINIMIZE_APP,
  TOGGLE_MAXIMIZE_APP,
  FOCUS_ICON,
  SELECT_ICONS,
  FOCUS_DESKTOP,
  START_SELECT,
  END_SELECT,
  POWER_OFF,
  CANCEL_POWER_OFF,
} from './constants/actions';
import { FOCUSING, POWER_STATE } from './constants';
import { defaultIconState, defaultAppState, appSettings } from './apps';
import Modal from './Modal';
import Footer from './Footer';
import Windows from './Windows';
import Icons from './Icons';
import { DashedBox } from 'components';
import BSoD from 'components/BSoD';
import Clippy from 'components/Clippy';
import DesktopContextMenu from 'components/DesktopContextMenu';
import MatrixScreensaver from 'components/Screensaver';
import ShutdownScreen from 'components/ShutdownScreen';

import errorSoundSrc from 'assets/sounds/error.wav';
import blissWallpaper from 'assets/bliss.jpg';

const initState = {
  apps: defaultAppState,
  nextAppID: defaultAppState.length,
  nextZIndex: defaultAppState.length,
  focusing: FOCUSING.WINDOW,
  icons: defaultIconState,
  selecting: false,
  powerState: POWER_STATE.START,
};

function reducer(state, action = { type: '' }) {
  switch (action.type) {
    case ADD_APP:
      return {
        ...state,
        apps: [
          ...state.apps,
          {
            ...action.payload,
            id: state.nextAppID,
            zIndex: state.nextZIndex,
          },
        ],
        nextAppID: state.nextAppID + 1,
        nextZIndex: state.nextZIndex + 1,
        focusing: FOCUSING.WINDOW,
      };
    case DEL_APP:
      return {
        ...state,
        apps: state.apps.filter(app => app.id !== action.payload),
        focusing:
          state.apps.length > 1 ? FOCUSING.WINDOW : FOCUSING.DESKTOP,
      };
    case FOCUS_APP: {
      const targetApp = state.apps.find(
        app => app.id === action.payload,
      );
      if (!targetApp) return state;
      const updatedApps = state.apps.map(app => {
        if (app.id === action.payload) {
          return { ...app, minimized: false, zIndex: state.nextZIndex };
        }
        return app;
      });
      return {
        ...state,
        apps: updatedApps,
        nextZIndex: state.nextZIndex + 1,
        focusing: FOCUSING.WINDOW,
      };
    }
    case MINIMIZE_APP:
      return {
        ...state,
        apps: state.apps.map(app =>
          app.id === action.payload ? { ...app, minimized: true } : app,
        ),
      };
    case TOGGLE_MAXIMIZE_APP:
      return {
        ...state,
        apps: state.apps.map(app =>
          app.id === action.payload
            ? { ...app, maximized: !app.maximized }
            : app,
        ),
      };
    case FOCUS_ICON:
      return {
        ...state,
        focusing: FOCUSING.ICON,
        icons: state.icons.map(icon => ({
          ...icon,
          isFocus: icon.id === action.payload,
        })),
      };
    case SELECT_ICONS:
      return {
        ...state,
        focusing: FOCUSING.ICON,
        icons: state.icons.map(icon => ({
          ...icon,
          isFocus: action.payload.includes(icon.id),
        })),
      };
    case FOCUS_DESKTOP:
      return {
        ...state,
        focusing: FOCUSING.DESKTOP,
        icons: state.icons.map(icon => ({
          ...icon,
          isFocus: false,
        })),
      };
    case START_SELECT:
      return {
        ...state,
        focusing: FOCUSING.DESKTOP,
        selecting: action.payload,
        icons: state.icons.map(icon => ({
          ...icon,
          isFocus: false,
        })),
      };
    case END_SELECT:
      return {
        ...state,
        selecting: false,
      };
    case POWER_OFF:
      return {
        ...state,
        powerState: action.payload,
      };
    case CANCEL_POWER_OFF:
      return {
        ...state,
        powerState: POWER_STATE.START,
      };
    default:
      return state;
  }
}

// Virus popup messages - escalating passive-aggression + Limewire-era comedy
// Phone: 0118 359 2220 | Website: deadsimplecomputing.co.uk
const virusMessages = [
  { title: 'Windows Security Alert', message: 'Your PC may be at risk!\n\nAntivirus software might not be installed.\nClick OK to fix this problem.' },
  { title: 'WARNING - System Alert', message: 'Your computer is infected with Spyware!\n\n47 threats detected. Your passwords and\nbank details may be at risk.\n\nClick OK to scan now.' },
  { title: 'Norton Antivirus', message: 'Your Norton subscription has expired!\n\nYour computer is UNPROTECTED.\nRenew now for only GBP89.99/year.\n\n...or call Dead Simple Computing\non 0118 359 2220 for actual security.' },
  { title: 'Congratulations!!!', message: 'YOU ARE THE 1,000,000th VISITOR!\n\nClick OK to claim your FREE iPod Nano!\n\n(This is definitely not a virus.)' },
  { title: 'BonziBuddy', message: 'Hi! I\'m Bonzi!\n\nI can help you search the web, send\nemail, and download FREE smileys!\n\nI also installed 14 toolbars\nand changed your homepage. You\'re welcome.' },
  { title: 'LimeWire Pro', message: 'linkin_park_numb.exe has finished downloading!\n\nFile size: 34 KB (seems legit)\n\nWould you also like to download\nFREE_SCREENSAVERS_2005.exe?' },
  { title: 'Kazaa Media Desktop', message: 'Your download is complete!\n\nmicrosoft_office_2003_keygen.exe\n\nThis file may contain malware.\n\nWho are we kidding, it definitely does.' },
  { title: 'FREE Smileys!!!', message: 'Download 10,000 FREE MSN Smileys!\n\nMake your friends JEALOUS with\nexclusive animated emoticons!\n\nAlso includes: WeatherBug,\nMyWebSearch Toolbar, and a\n\"small\" background process\ncalled svchost_helper.exe' },
  { title: 'bearshare.exe', message: 'BearShare has detected 847\nshared files on your computer.\n\nThese are now available for\ndownload by everyone on the internet.\n\nIncluding your tax returns.\n\nEnjoy!' },
  { title: 'PC Health Check', message: 'CRITICAL WARNING!\n\nYour PC has 2,847 Registry Errors!\n\nPerformance reduced by 94%.\n\nPurchase WinFixer Pro for only $49.99\nto fix all errors instantly!\n\n(Do NOT close this window.)' },
  { title: 'Windows Genuine Advantage', message: 'This copy of Windows may not be genuine.\n\nYou may be a victim of software\ncounterfeiting.\n\nTo be fair, you\'re also a victim of\nnot having an IT support company.' },
  { title: 'Gator / GAIN', message: 'Gator has saved your passwords\nand shopping preferences!\n\nWe\'re also tracking your browsing\nhistory for \"personalised offers.\"\n\nYou agreed to this. Probably.\nDo you read EULAs? No, you don\'t.' },
  { title: 'CoolWebSearch', message: 'Your homepage has been changed to\ncoolwebsearch.com!\n\nYour default search engine is now\ncoolwebsearch.com!\n\nEnjoy your 47 new bookmarks!\n\nYou didn\'t want Google anyway.' },
  { title: 'FREE Cursor Pack!!!', message: 'Download 500+ FREE animated cursors!\n\nIncludes: Spinning Globe, Dancing Baby,\nFlaming Skull, and Clippy!\n\nAlso installs: Hotbar, MyWebSearch,\nand something called \"trojan.gen\"' },
  { title: 'Hotbar', message: 'Hotbar has been successfully installed!\n\nEnjoy themed browser skins and\nanimated email stationery!\n\nYour browser may be slightly slower.\nBy \"slightly\" we mean \"unusable.\"' },
  { title: 'Dead Simple Computing', message: 'Still here? Still clicking OK?\n\nManaged IT | Cyber Security | Microsoft 365\n\nStop getting hacked. Start getting sorted.\n\n0118 359 2220\ndeadsimplecomputing.co.uk' },
  { title: 'McAfee SecurityCenter', message: 'McAfee has detected a problem:\n\nYOUR ENTIRE COMPUTER.\n\nWould you like McAfee to fix this?\n\n(McAfee cannot fix this.\nNobody can fix this.\nExcept maybe an IT company.\nJust saying.)' },
  { title: 'WARNING - System Critical', message: 'Your computer now has MORE viruses\nthan actual files.\n\nAt this point, the viruses are running\nyour business.\n\nMaybe ring an IT company?\nJust a thought. 0118 359 2220.' },
  { title: 'Internet Explorer', message: 'Internet Explorer has detected\n47 pop-up windows.\n\nWould you like to block pop-ups?\n\nHa. Good one.\nYou can\'t even block your own\nbad decisions.' },
  { title: 'Windows Firewall', message: 'Windows Firewall is turned OFF.\n\nYour computer is as protected as\na chocolate teapot.\n\nWe recommend calling someone\nwho actually knows what a firewall is.\n\n0118 359 2220' },
  { title: 'Automatic Updates', message: 'You have 847 critical updates pending.\n\nYou\'ve been clicking \"Remind Me Later\"\nsince 2004.\n\nThat\'s commitment, we\'ll give you that.\nWrong kind of commitment, but still.' },
  { title: 'Dead Simple Computing', message: 'Look, we\'ve been very patient.\n\nYou\'ve clicked OK on about 20 popups.\nYour PC has 47 toolbars.\nYour homepage is a Russian search engine.\nYour mouse cursor is a flaming skull.\n\nCall us: 0118 359 2220\nBefore it\'s too late.' },
  { title: 'Spyware Alert', message: 'Fun fact: your nan could hack\nthis PC with a flip phone.\n\nActual security professionals are\navailable on 0118 359 2220.\n\nOr keep clicking OK. We\'re not\nyour mum.' },
  { title: 'System Event Notification', message: 'Your antivirus has officially\ngiven up.\n\nIt left a note:\n\"I tried. I really did.\nBut this PC is beyond saving.\nPlease call a professional.\"\n\nDead Simple Computing\n0118 359 2220' },
  { title: 'Task Manager', message: 'You have 247 processes running.\n\n243 of them are malware.\n3 are toolbars.\n1 is Windows.\n\nWindows is losing.\n\nCall for backup: 0118 359 2220' },
  { title: 'Clippy', message: 'It looks like you\'re trying to\nrun a business.\n\nWould you like help with that?\n\nBecause based on this PC,\nyou definitely need some.\n\ndeadsimplecomputing.co.uk\n0118 359 2220' },
  { title: 'HMRC Tax Portal', message: 'IMPORTANT: Your Self Assessment\nis overdue.\n\nYour accountant called.\nThey\'re crying.\n\nMaybe also call Dead Simple Accounting?\ndeadsimpleaccounting.co.uk\n\n(Not a real HMRC message.\nBut the crying accountant bit\nmight be accurate.)' },
  { title: 'Error Report', message: 'Windows has encountered a problem\nand needs to close.\n\n\"The problem\" is whoever is\nresponsible for the IT here.\n\nThat might be you.\nSorry not sorry.\n\nDead Simple Computing\n0118 359 2220' },
  { title: 'BonziBuddy Returns', message: 'Remember me? I\'m back!\n\nI brought friends:\n- 3 more toolbars\n- A cryptocurrency miner\n- Something called \"totally_not_ransomware\"\n\nYour CPU is now at 247%.\nI didn\'t know that was possible either.' },
  { title: 'FINAL WARNING', message: 'CONGRATULATIONS!\n\nYour PC is now technically classified\nas malware itself.\n\nISO 27001 score: -4 out of 10\nCyber Essentials: LOL\nGDPR compliance: Wanted by the ICO\n\nDead Simple Computing\n0118 359 2220\ndeadsimplecomputing.co.uk\n\nWe fix this. It is literally our job.' },
];

// Max visible popups at any time
const MAX_VISIBLE_POPUPS = 6;

// Helper: spawn a single virus popup
function spawnPopup(dispatch, virus, muteRef) {
  if (!muteRef.current && Math.random() > 0.6) {
    try { new Audio(errorSoundSrc).play(); } catch (e) { }
  }
  const maxX = Math.max(200, window.innerWidth - 420);
  const maxY = Math.max(100, window.innerHeight - 250);
  const randX = 50 + Math.floor(Math.random() * maxX);
  const randY = 30 + Math.floor(Math.random() * maxY);
  dispatch({
    type: ADD_APP,
    payload: {
      ...appSettings.Error,
      header: {
        ...appSettings.Error.header,
        title: virus.title,
      },
      defaultOffset: { x: randX, y: randY },
      injectProps: { message: virus.message },
      multiInstance: true,
      isVirusPopup: true, // Tag so we can identify virus popups
    },
  });
}

function WinXP() {
  const [state, dispatch] = useReducer(reducer, initState);
  const ref = useRef(null);
  const mouse = useMouse(ref);
  const [soundMuted, setSoundMuted] = React.useState(false);
  const muteRef = useRef(false);
  const nextPopupIndex = useRef(0); // Tracks next message to show
  const [showBSoD, setShowBSoD] = useState(false);
  const [isShuttingDown, setIsShuttingDown] = useState(false);
  const [screensaverActive, setScreensaverActive] = useState(false);
  const [popupsStopped, setPopupsStopped] = useState(false);
  const popupsStoppedRef = useRef(false);

  // Shuffle virus messages on mount so order is always random
  const shuffledMessages = useRef(null);
  if (!shuffledMessages.current) {
    const arr = [...virusMessages];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    shuffledMessages.current = arr;
  }

  // Open Help and Support on first load so users know what's available
  useEffect(() => {
    dispatch({ type: ADD_APP, payload: appSettings['Help and Support'] });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for xp-open-app custom events (from MyDocuments, etc.)
  useEffect(() => {
    function handleOpenApp(e) {
      const appName = e.detail;
      if (appName && appSettings[appName]) {
        dispatch({ type: ADD_APP, payload: appSettings[appName] });
      }
    }
    window.addEventListener('xp-open-app', handleOpenApp);
    return () => window.removeEventListener('xp-open-app', handleOpenApp);
  }, []);

  const focusedAppId = getFocusedAppId();
  const onFocusApp = useCallback(id => {
    dispatch({ type: FOCUS_APP, payload: id });
  }, []);
  const onMaximizeWindow = useCallback(
    id => {
      if (focusedAppId === id) {
        dispatch({ type: TOGGLE_MAXIMIZE_APP, payload: id });
      }
    },
    [focusedAppId],
  );
  const onMinimizeWindow = useCallback(
    id => {
      if (focusedAppId === id) {
        dispatch({ type: MINIMIZE_APP, payload: id });
      }
    },
    [focusedAppId],
  );
  const onCloseApp = useCallback(
    id => {
      if (focusedAppId === id) {
        dispatch({ type: DEL_APP, payload: id });
      }
    },
    [focusedAppId],
  );

  // Toggle mute
  const toggleMute = useCallback(() => {
    setSoundMuted(prev => {
      muteRef.current = !prev;
      return !prev;
    });
  }, []);

  // BSoD keyboard shortcut: Ctrl+Shift+B
  useEffect(() => {
    function handleKeyDown(e) {
      if (e.ctrlKey && e.shiftKey && (e.key === 'b' || e.key === 'B')) {
        e.preventDefault();
        setShowBSoD(true);
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [focusedAppId]);

  // Virus popup system — randomised order, 50s delay, slow drip-feed
  useEffect(() => {
    const msgs = shuffledMessages.current;
    const initialTimers = [];
    const INITIAL_DELAY = 20000; // 20 seconds before first popup
    for (let i = 0; i < MAX_VISIBLE_POPUPS && i < msgs.length; i++) {
      // First at 50s, then each subsequent 15-30s after that
      const delay = INITIAL_DELAY + i * (15000 + Math.floor(Math.random() * 15000));
      initialTimers.push(
        setTimeout(() => {
          if (!popupsStoppedRef.current) {
            spawnPopup(dispatch, msgs[i], muteRef);
          }
          nextPopupIndex.current = i + 1;
        }, delay)
      );
    }
    return () => initialTimers.forEach(t => clearTimeout(t));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Watch for popup closures — when one is closed, spawn next (slowly)
  const prevAppCountRef = useRef(0);
  useEffect(() => {
    const msgs = shuffledMessages.current;
    const currentVirusCount = state.apps.filter(a => a.isVirusPopup).length;
    const prevCount = prevAppCountRef.current;
    prevAppCountRef.current = currentVirusCount;

    // If virus popup count decreased (user closed one), spawn replacement after 8-15s
    if (prevCount > 0 && currentVirusCount < prevCount && currentVirusCount < MAX_VISIBLE_POPUPS) {
      const idx = nextPopupIndex.current;
      if (idx < msgs.length) {
        const delay = 8000 + Math.floor(Math.random() * 7000); // 8-15s before next
        const timer = setTimeout(() => {
          if (!popupsStoppedRef.current) {
            spawnPopup(dispatch, msgs[idx], muteRef);
          }
          nextPopupIndex.current = idx + 1;
        }, delay);
        return () => clearTimeout(timer);
      } else {
        // Exhausted all messages — reshuffle and loop
        const arr = [...virusMessages];
        for (let i = arr.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [arr[i], arr[j]] = [arr[j], arr[i]];
        }
        shuffledMessages.current = arr;
        nextPopupIndex.current = 0;
        const delay = 10000 + Math.floor(Math.random() * 10000);
        const timer = setTimeout(() => {
          if (!popupsStoppedRef.current) {
            spawnPopup(dispatch, arr[0], muteRef);
          }
          nextPopupIndex.current = 1;
        }, delay);
        return () => clearTimeout(timer);
      }
    }
  }, [state.apps]); // eslint-disable-line react-hooks/exhaustive-deps

  function onMouseDownFooterApp(id) {
    if (focusedAppId === id) {
      dispatch({ type: MINIMIZE_APP, payload: id });
    } else {
      dispatch({ type: FOCUS_APP, payload: id });
    }
  }
  function onMouseDownIcon(id) {
    dispatch({ type: FOCUS_ICON, payload: id });
  }
  function onDoubleClickIcon(component) {
    const appSetting = Object.values(appSettings).find(
      setting => setting.component === component,
    );
    if (appSetting) {
      dispatch({ type: ADD_APP, payload: appSetting });
    }
  }
  function getFocusedAppId() {
    if (state.focusing !== FOCUSING.WINDOW) return -1;
    const focusedApp = [...state.apps]
      .sort((a, b) => b.zIndex - a.zIndex)
      .find(app => !app.minimized);
    return focusedApp ? focusedApp.id : -1;
  }
  function onMouseDownFooter() {
    dispatch({ type: FOCUS_DESKTOP });
  }
  function onClickMenuItem(o) {
    // Map menu item text to appSettings keys
    const menuMap = {
      'Internet': 'Internet Explorer',
      'Dead Simple Computing': 'Internet Explorer',
      'Dead Simple Accounting': 'DSA Internet Explorer',
      'Minesweeper': 'Minesweeper',
      'My Computer': 'My Computer',
      'Notepad': 'Notepad',
      'passwords.txt': 'passwords.txt',
      'My Finances.xls': 'My Finances.xls',
      'Winamp': 'Winamp',
      'Paint': 'Paint',
      'MSN Messenger': 'MSN Messenger',
      'Outlook Express': 'Outlook Express',
      'Security Checklist': 'Security Checklist',
      'Security Center': 'Security Center',
      'Command Prompt': 'Command Prompt',
      'Windows Update': 'Windows Update',
      'Sticky Notes': 'Sticky Notes',
      'Compliance Calendar': 'Compliance Calendar',
      // Right-side Start Menu items
      'My Pictures': 'My Pictures',
      'My Documents': 'My Documents',
      'My Music': 'Winamp',
      'Control Panel': 'Security Center',
      'Help and Support': 'Help and Support',
      'Search': 'My Computer',
      'Run...': 'Command Prompt',
      'Printers and Faxes': 'My Computer',
      'Set Program Access and Defaults': 'Security Center',
      // 'Solitaire': 'Solitaire', // Removed — Solitaire no longer available
      'Task Manager': 'Task Manager',
      'Client Reviews': 'Testimonials',
      'Calculator': 'Calculator',
      'Pinball': 'Pinball',
      'Recycle Bin': 'Recycle Bin',
      'Properties': 'Display Properties',
      'MSN Messenger': 'MSN Messenger',
      'Windows Messenger': 'MSN Messenger',
      'Privacy Policy': 'Privacy Policy',
      'Terms of Use': 'Privacy Policy',
      'Web Design & Hosting': 'Web Design',
      'Cyber Awareness 2004.ppt': 'Cyber Awareness',
    };
    if (o === 'Log Off')
      dispatch({ type: POWER_OFF, payload: POWER_STATE.LOG_OFF });
    else if (o === 'Turn Off Computer') {
      setIsShuttingDown(true);
    }
    else if (menuMap[o])
      dispatch({ type: ADD_APP, payload: appSettings[menuMap[o]] });
    else
      dispatch({
        type: ADD_APP,
        payload: {
          ...appSettings.Error,
          injectProps: { message: 'C:\\\nApplication not found' },
        },
      });
  }
  function onMouseDownDesktop(e) {
    if (e.target === e.currentTarget)
      dispatch({
        type: START_SELECT,
        payload: { x: mouse.docX, y: mouse.docY },
      });
  }
  function onMouseUpDesktop(e) {
    dispatch({ type: END_SELECT });
  }
  const onIconsSelected = useCallback(
    iconIds => {
      dispatch({ type: SELECT_ICONS, payload: iconIds });
    },
    [dispatch],
  );
  function onClickModalButton(text) {
    dispatch({ type: CANCEL_POWER_OFF });
    dispatch({
      type: ADD_APP,
      payload: appSettings.Error,
    });
  }
  function onModalClose() {
    dispatch({ type: CANCEL_POWER_OFF });
  }
  return (
    <Container
      ref={ref}
      onMouseUp={onMouseUpDesktop}
      onMouseDown={onMouseDownDesktop}
      state={state.powerState}
    >
      <Icons
        icons={state.icons}
        onMouseDown={onMouseDownIcon}
        onDoubleClick={onDoubleClickIcon}
        displayFocus={state.focusing === FOCUSING.ICON}
        appSettings={appSettings}
        mouse={mouse}
        selecting={state.selecting}
        setSelectedIcons={onIconsSelected}
      />
      <DashedBox startPos={state.selecting} mouse={mouse} />
      <Windows
        apps={state.apps}
        onMouseDown={onFocusApp}
        onClose={onCloseApp}
        onMinimize={onMinimizeWindow}
        onMaximize={onMaximizeWindow}
        focusedAppId={focusedAppId}
      />
      <Footer
        apps={state.apps}
        onMouseDownApp={onMouseDownFooterApp}
        focusedAppId={focusedAppId}
        onMouseDown={onMouseDownFooter}
        onClickMenuItem={onClickMenuItem}
        soundMuted={soundMuted}
        onToggleMute={toggleMute}
      />
      {state.powerState !== POWER_STATE.START && (
        <Modal
          onClose={onModalClose}
          onClickButton={onClickModalButton}
          mode={state.powerState}
        />
      )}
      {/* Desktop features */}
      <DesktopContextMenu
        onProperties={() => onClickMenuItem('Properties')}
        onScreensaver={() => setScreensaverActive(true)}
      />
      <Clippy idleTimeout={60000} />
      <MatrixScreensaver active={screensaverActive} onDismiss={() => setScreensaverActive(false)} />
      {/* Popup kill switch — bottom-right corner, above taskbar */}
      {!popupsStopped && (
        <PopupKillSwitch onClick={() => {
          setPopupsStopped(true);
          popupsStoppedRef.current = true;
          // Close all existing virus popups
          state.apps.filter(a => a.isVirusPopup).forEach(a => {
            dispatch({ type: DEL_APP, payload: a.id });
          });
        }}>
          🛡️ Stop Popups
        </PopupKillSwitch>
      )}
      {popupsStopped && (
        <PopupKillSwitch className="stopped">
          ✅ Popups Stopped
        </PopupKillSwitch>
      )}
      {showBSoD && <BSoD onDismiss={() => setShowBSoD(false)} />}
      {isShuttingDown && <ShutdownScreen />}
    </Container>
  );
}

const powerOffAnimation = keyframes`
  0% {
    filter: brightness(1) grayscale(0);
  }
  30% {
    filter: brightness(1) grayscale(0);
  }
  100% {
    filter: brightness(0.6) grayscale(1);
  }
`;
const animation = {
  [POWER_STATE.START]: '',
  [POWER_STATE.TURN_OFF]: powerOffAnimation,
  [POWER_STATE.LOG_OFF]: powerOffAnimation,
};

const Container = styled.div`
  @import url('https://fonts.googleapis.com/css?family=Noto+Sans');
  font-family: Tahoma, 'Noto Sans', sans-serif;
  height: 100%;
  overflow: hidden;
  position: relative;
  background: url(${blissWallpaper}) center center / cover no-repeat;
  background-color: #3a6bc5;
  animation: ${({ state }) => animation[state]} 5s forwards;
  *:not(input):not(textarea) {
    user-select: none;
  }
`;

const PopupKillSwitch = styled.div`
  position: fixed;
  bottom: 40px;
  right: 8px;
  z-index: 99;
  background: rgba(0, 0, 0, 0.5);
  color: #fff;
  padding: 4px 10px;
  border-radius: 4px;
  font-family: Tahoma, sans-serif;
  font-size: 11px;
  cursor: pointer;
  backdrop-filter: blur(4px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.2s;
  &:hover {
    background: rgba(0, 0, 0, 0.7);
  }
  &.stopped {
    cursor: default;
    background: rgba(0, 100, 0, 0.5);
    border-color: rgba(0, 200, 0, 0.3);
  }
`;

export default WinXP;
